import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CircleCheck, CircleX, ImagePlus, Users } from "lucide-react";
import { fromJS } from "immutable";
import { useQuery } from "@tanstack/react-query";
import { endOfDay, formatISO, startOfDay } from "date-fns";
import ImageView from "./ImageView";
import { getBaseUrl } from "../../api/api";
import ImageUploadDialog from "./ImageUploadDialog";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import AIContentAction from "../../actions/AIContentAction";
import { searchSocialPosts, updatePostField } from "../../api/AIContentApi";
import LayoutAction from "../../actions/LayoutAction";
import AppSocial from "../../rules/components/AppSocial";
import RuleAction from "../../actions/RuleAction";
import { SmallLoading } from "../../main/Loading";

const ApplyImageAllPostInfo = ({ projectId, startDate, endDate }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));

  useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, [dispatch]);

  const { data, isLoading } = useQuery({
    queryKey: ["socialPosts", projectId, startDate, endDate],
    queryFn: async () => {
      const rangeStart = startOfDay(new Date(startDate));
      const rangeEnd = endOfDay(new Date(endDate));
      return await searchSocialPosts({
        projectId,
        page: 0,
        pageSize: 500,
        filters: {
          start: {
            operator: "dateRange",
            values: [formatISO(rangeStart), formatISO(rangeEnd)],
          },
        },
        fields: [],
        sorts: [],
      });
    },
    enabled: !!startDate && !!endDate,
    staleTime: 60 * 1000,
  });

  if (!startDate || !endDate) {
    return (
      <div>{t("Do you want to set this image for all posts?")}</div>
    );
  };

  const items = fromJS(data?.pageResult?.content || []);
  const socialApps = ["facebook", "instagram", "twitter", "linkedin", "threads"];

  return (
    <div>
      <div className="pb-2">{t("Do you want to set this image for all posts?")}</div>
      <div className="pb-2 text-sm font-semibold text-gray-900">{`${startDate} to ${endDate}`}</div>
      <div className="px-2 text-left grid grid-cols-12 gap-2">
        {socialApps.map((socialApp) => {
          const app = appLinks.toJS().find((item: any) => item.app === socialApp);
          const isConnected = app?.connected || false;
          const totalPost = items.filter((item) =>
            item.getIn(["data", "platform", "value"]) === socialApp
            && item.getIn(["data", "status", "value"]) === "DRAFT"
          ).size;
          return (
            <div
              key={socialApp}
              className="col-span-12 mb-2 flex items-center justify-between"
            >
              <AppSocial
                appId={socialApp}
                connected={isConnected}
                onConnect={() => {
                  navigate(`/workspaces/${projectId}/settings/apps?key=Apps&appId=${app.app}`);
                }}
              />
              {isLoading ? (
                <div className="pr-2">
                  <SmallLoading />
                </div>
              ) : (
                <div className="text-sm font-semibold text-gray-900">
                  {totalPost > 1 ? `${totalPost} posts` : `${totalPost} post`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PostImage = ({ postDetail, fileImages, hostImageDetail, updateDefaultImage, onRemoveImage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [imageThumbnails, setImageThumbnails] = useState([]);

  const aiSetting =
    useSelector((state: any) => state.projectReducer.getIn(["project", "aiSetting"])) || fromJS({});

  const id = postDetail.get("id") || "";
  const status = postDetail.getIn(["data", "status", "value"]);
  const mediaId = postDetail.getIn(["data", "mediaId", "value"]);
  const mediaType = postDetail.getIn(["data", "mediaType", "value"]);

  const isEdit = status === "DRAFT";

  useEffect(() => {
    setImageThumbnails((prev) => {
      const hostImageAll = fileImages.toJS();
      if (prev.length === 0) {
        const result = [];
        let isExist = false;
        for (let i = 0; i < 6; i++) {
          if (hostImageAll.length > i) {
            if (hostImageAll[i]?.event?.data?.fileName === hostImageDetail?.event?.data?.fileName) {
              isExist = true;
            }
            result.push(hostImageAll[i]);
          } else {
            result.push({});
          }
        }
        if (!isExist && hostImageDetail) {
          result.pop();
          result.unshift(hostImageDetail);
        }
        return result;
      }
      const existedImage = prev.find(
        (item) => item?.event?.data?.fileName === hostImageDetail?.event?.data?.fileName
      );
      if (existedImage) {
        return prev;
      }
      return hostImageDetail ? [hostImageDetail, ...prev.slice(0, 5)] : prev;
    });
  }, [hostImageDetail, fileImages]);

  const updateImageAllPosts = (mediaIdData) => {
    updatePostField(projectId, {
      startDate: aiSetting.get("startDate"),
      endDate: aiSetting.get("endDate"),
      fieldId: "mediaId",
      fieldValue: mediaIdData,
      fieldType: "string",
    })
      .then(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Update Image"),
            type: "success",
            message: t("Update all posts successfully"),
          })
        );
      })
      .catch(() => { });
  };

  const handleSubmit = async () => {
    if (showConfirm.type === "UPDATE_IMAGE_ALL_POST") {
      updateImageAllPosts(mediaId);
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleSaveImage = (mediaIdData, mediaTypeData, isApplyAll) => {
    if (!mediaIdData || !mediaTypeData) return;
    setImageThumbnails([]);
    updateDefaultImage(mediaIdData, mediaTypeData, [mediaIdData]);
    if (isApplyAll) {
      updateImageAllPosts(mediaIdData);
    }
    setShowImageDialog(false);
  };

  const handleSelectImage = (mediaIdData, mediaTypeData, mediaIdsData) => {
    if (mediaId !== mediaIdData) {
      if (isEdit) {
        updateDefaultImage(mediaIdData, mediaTypeData, mediaIdsData);
      } else {
        dispatch(
          AIContentAction.updatePostDetail({
            ...postDetail.toJS(),
            data: {
              ...postDetail.get("data").toJS(),
              mediaId: { value: mediaIdData, label: "mediaId", type: "string" },
              mediaType: {
                value: mediaTypeData,
                label: "mediaType",
                type: "string",
              },
              mediaIds: {
                value: mediaIdsData,
                label: "mediaIds",
                type: "stringArray",
              },
            },
          })
        );
        dispatch(
          AIContentAction.updateSocialPosts({
            ...postDetail.toJS(),
            data: {
              ...postDetail.get("data").toJS(),
              mediaId: { value: mediaIdData, label: "mediaId", type: "string" },
              mediaType: {
                value: mediaTypeData,
                label: "mediaType",
                type: "string",
              },
              mediaIds: {
                value: mediaIdsData,
                label: "mediaIds",
                type: "stringArray",
              },
            },
          })
        );
      }
    }
  };

  const handleApplyAllPosts = () => {
    if (!mediaId || !mediaType) return;
    setShowConfirm({
      show: true,
      title: t("Apply to all posts"),
      description: (
        <ApplyImageAllPostInfo
          projectId={projectId}
          startDate={aiSetting.get("startDate")}
          endDate={aiSetting.get("endDate")}
        />
      ),
      type: "UPDATE_IMAGE_ALL_POST",
    });
  };

  const renderEmptyState = () => {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div>
          <img
            src={`${getBaseUrl()}/static/images/empty-state.png`}
            className="h-40 object-contain"
            alt="Empty state"
          />
        </div>
        <div className="pt-2 text-gray-600">{t("Please upload your own images")}</div>
      </div>
    );
  };

  const renderImage = () => {
    if (!mediaId) {
      return renderEmptyState();
    }
    return (
      <>
        <ImageView fileName={mediaId} className="w-full h-full object-contain" />
        {isEdit ? (
          <div className="absolute top-2 right-2">
            <div className="flex items-center gap-2">
              <div
                className="bg-blue-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                onClick={() => handleApplyAllPosts()}
              >
                <CircleCheck className="w-3 h-3 text-white" />
                <span className="text-xs font-medium text-white">{t("Apply to all posts")}</span>
              </div>
              <div
                className="bg-red-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                onClick={onRemoveImage}
              >
                <CircleX className="w-3 h-3 text-white" />
                <span className="text-xs font-medium text-white">{t("Remove")}</span>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  const renderImageThumbnail = () => {
    return (
      <>
        {fromJS(imageThumbnails).map((item, index) => (
          <div
            key={`image-${index}`}
            className={`w-[50px] h-[50px] bg-white cursor-pointer rounded-md relative ${item.getIn(["event", "data", "fileName"]) === mediaId
              ? "border-2 border-blue-500"
              : "border border-gray-200"
              }`}
            onClick={() => {
              if (!!item.getIn(["event", "data", "fileName"])) {
                handleSelectImage(item.getIn(["event", "data", "fileName"]), "HOST", [
                  item.getIn(["event", "data", "fileName"]),
                ]);
              }
            }}
          >
            {!!item.getIn(["event", "data", "fileName"]) ? (
              <ImageView
                fileName={item.getIn(["event", "data", "fileName"])}
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={`${getBaseUrl()}/static/images/upload-images.png`}
                className="mt-1.5 p-1 object-contain"
                alt="Empty state"
              />
            )}
            {item.getIn(["event", "data", "visibility"]) === "Shared" ? (
              <div className="w-4 h-4 flex items-center justify-center absolute -bottom-2 left-4 rounded-full bg-gray-600">
                <Users className="w-3 h-3 text-white" />
              </div>
            ) : null}
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <div className="flex gap-5">
        <div className="relative w-[500px] h-[500px] border border-gray-200 rounded-lg overflow-hidden">
          {renderImage()}
        </div>
      </div>

      <div className="p-2 flex items-center justify-center gap-3 bg-blue-100 rounded-lg">
        {renderImageThumbnail()}
        {isEdit ? (
          <div
            className="w-[50px] h-[50px] rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center cursor-pointer"
            onClick={() => {
              setShowImageDialog(true);
            }}
          >
            <ImagePlus className="w-4 h-4 text-gray-600" />
          </div>
        ) : null}
      </div>

      {showImageDialog && (
        <ImageUploadDialog
          projectId={projectId}
          itemId={id}
          open={showImageDialog}
          onClose={() => {
            setImageThumbnails(() => {
              const hostImageAll = fileImages.toJS();
              const result = [];
              let isExist = false;
              for (let i = 0; i < 6; i++) {
                if (hostImageAll.length > i) {
                  if (
                    hostImageAll[i]?.event?.data?.fileName ===
                    hostImageDetail?.event?.data?.fileName
                  ) {
                    isExist = true;
                  }
                  result.push(hostImageAll[i]);
                } else {
                  result.push({});
                }
              }
              if (!isExist && hostImageDetail) {
                result.pop();
                result.unshift(hostImageDetail);
              }
              return result;
            });
            setShowImageDialog(false);
          }}
          onSave={handleSaveImage}
        />
      )}

      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={<OctagonAlertIcon />}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default PostImage;
